/* Ruby Rose — header behaviour and iconography.
   Jobs:
   1. Force Horizon's header into drawer mode (the mockup has no text nav).
   2. Move the language selector between cart and account, per the mockup.
   3. Replace Horizon's glyphs with the mockup's own icon set.
   4. Inject the wishlist heart + the always-visible "0" count badges.

   Icons are swapped in JS rather than by editing header.liquid, so a future
   Horizon update cannot clobber them. */

(function () {
  // 1.45 in a 24-unit viewBox at a 39px box renders ~2.4px, matching the
  // mockup's line weight; 1.7 was noticeably heavier than the design.
  const S = 'stroke="currentColor" stroke-width="1.45" stroke-linecap="round" stroke-linejoin="round"';

  // Every glyph is drawn to fill its 24-unit viewBox edge to edge. Earlier
  // versions were inset by varying amounts, so at one box size the icons came
  // out different visual sizes — that is what made the row look incoherent.
  const ICONS = {
    heart:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M12 21.7C12 21.7 1.6 15.3 1.6 8.2A5.5 5.5 0 0 1 12 5.3a5.5 5.5 0 0 1 10.4 2.9c0 7.1-10.4 13.5-10.4 13.5Z" ${S}/>
      </svg>`,

    // Trolley: slanted handle, basket, two wheels — as drawn in the mockup.
    cart:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M1.4 2.3h2.7l3.4 12.6h11.2l2.6-9.1H6.2" ${S}/>
        <circle cx="9.4" cy="19.6" r="1.9" ${S}/>
        <circle cx="17.4" cy="19.6" r="1.9" ${S}/>
      </svg>`,

    // The 文A translate glyph, drawn as paths so it never depends on a CJK font.
    language:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M9.1 1.6 10.8 4.2" ${S}/>
        <path d="M1.6 5.8h14.2" ${S}/>
        <path d="M12.4 7.8c-1.9 4.4-5 7.9-9 10.6" ${S}/>
        <path d="M7.2 7.8c2.1 4.2 4.2 6.9 7.2 9" ${S}/>
        <path d="M14.1 22.4 18 11.6 21.9 22.4" ${S}/>
        <path d="M15.6 18.8h4.8" ${S}/>
      </svg>`,

    account:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <circle cx="12" cy="7.4" r="5.1" ${S}/>
        <path d="M2.4 22.2a9.6 9.6 0 0 1 19.2 0" ${S}/>
      </svg>`,

    search:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <circle cx="10" cy="10" r="8.2" ${S}/>
        <path d="m15.9 15.9 6.3 6.3" ${S}/>
      </svg>`,

    // Three separate paths rather than one multi-subpath `d`: the combined
    // form rendered only two of the three rules.
    menu:
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M1.6 8h20.8" ${S}/>
        <path d="M1.6 12h20.8" ${S}/>
        <path d="M1.6 16h20.8" ${S}/>
      </svg>`,
  };

  function badge(kind, count) {
    const el = document.createElement("span");
    el.className = "rr-badge";
    el.dataset.rrBadge = kind;
    el.textContent = count;
    return el;
  }

  /* Horizon keeps the real count in its own .cart-bubble and re-renders it on
     every cart change. Reading from there keeps our badge honest without a
     second /cart.js round trip. The bubble itself is hidden in CSS rather than
     removed, so Horizon can carry on updating it. Its text reads like
     "Total items in cart: 2 2", hence taking the last run of digits. */
  function cartCount() {
    const bubble = document.querySelector("cart-icon .cart-bubble");
    if (!bubble) return "0";
    const digits = bubble.textContent.match(/\d+/g);
    return digits ? digits[digits.length - 1] : "0";
  }

  /* A count published by the bag panel (see "ruby:cart:count" below) is
     fresher than Horizon's bubble after an add through our own /cart/add.js.
     It is kept until the bubble itself changes; without this, the header's
     MutationObserver re-ran syncCartBadge on the badge's own text change and
     put the stale bubble value ("0") straight back. */
  let freshCount = null;

  function syncCartBadge() {
    const el = document.querySelector('cart-icon [data-rr-badge="cart"]');
    if (!el) return;
    const bubble = cartCount();
    if (freshCount && bubble === freshCount.bubbleAt) {
      if (el.textContent !== freshCount.count) el.textContent = freshCount.count;
      return;
    }
    freshCount = null;
    if (el.textContent !== bubble) el.textContent = bubble;
  }

  /** Replace the first <svg> inside `host` with one of ours.
      Idempotent, and keyed off the rendered svg rather than the host: moving
      `.header__drawer` re-mounts Horizon's <header-drawer>, which re-renders
      its children and would otherwise restore the original glyph for good. */
  function swap(host, name) {
    if (!host) return;
    const svg = host.querySelector("svg");
    if (!svg || svg.dataset.rr === name) return;
    svg.outerHTML = ICONS[name];
    const fresh = host.querySelector("svg");
    if (fresh) fresh.dataset.rr = name;
  }

  function forceDrawer(header) {
    // Horizon's inline script sets this from touch/overflow detection and
    // re-runs on resize, so it has to be re-asserted.
    if (header.dataset.menuStyle !== "drawer") header.dataset.menuStyle = "drawer";
  }

  function injectWishlist(header) {
    const actions = header.querySelector("header-actions");
    if (!actions || actions.querySelector(".rr-header__wishlist")) return;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "rr-header__wishlist";
    button.setAttribute("aria-label", "Wishlist");
    button.innerHTML = ICONS.heart;
    // ruby-wishlist.js fills in the count; opening the panel is ruby-panels.js.
    button.appendChild(badge("wishlist", 0));
    actions.prepend(button);
  }

  /** The mockup order is heart, cart, language, account — but Horizon renders
      the language selector outside the actions cluster, so relocate it. */
  function relocateLanguage(header) {
    const actions = header.querySelector("header-actions");
    const loc = header.querySelector(".dropdown-localization");
    if (!actions || !loc || loc.dataset.rrMoved) return;

    loc.dataset.rrMoved = "1";
    loc.classList.remove("mobile:hidden");
    const cart = actions.querySelector("cart-drawer-component") || actions.firstElementChild;
    cart ? cart.after(loc) : actions.appendChild(loc);
  }

  /** Put all six controls in one flex row, in the mockup's sequence, by moving
      the real nodes rather than reordering with CSS `order`. Visual order and
      DOM order then agree, so keyboard tab order and screen readers follow the
      same sequence a sighted user sees. */
  function orderCluster(header) {
    const actions = header.querySelector("header-actions");
    if (!actions || actions.dataset.rrOrdered) return;

    const sequence = [
      actions.querySelector(".rr-header__wishlist"),
      actions.querySelector("cart-drawer-component"),
      actions.querySelector(".dropdown-localization"),
      actions.querySelector(".account-button"),
      header.querySelector("search-button.search-action:not(.search-action--hidden-on-drawer)") ||
        header.querySelector("search-button.search-action"),
      header.querySelector(".header__drawer"),
    ].filter(Boolean);

    sequence.forEach((el) => actions.appendChild(el));
    actions.dataset.rrOrdered = "1";
  }

  function applyIcons(header) {
    swap(header.querySelector("cart-icon .svg-wrapper"), "cart");
    swap(header.querySelector("search-button:not(.search-action--hidden-on-drawer) .svg-wrapper"), "search");
    swap(header.querySelector(".header__icon--menu .svg-wrapper"), "menu");
    swap(header.querySelector('.account-button span[slot="signed-out-avatar"][aria-hidden]'), "account");

    // Horizon renders the language picker as a native <select>, not a button.
    // Overlay the mockup's glyph and let the (transparent) select stay the real
    // control, so keyboard use and form submission keep working.
    const langWrap = header.querySelector(".dropdown-localization .language-selector");
    if (langWrap && !langWrap.dataset.rrIcon) {
      langWrap.insertAdjacentHTML("beforeend", `<span class="rr-lang-icon">${ICONS.language}</span>`);
      langWrap.dataset.rrIcon = "language";
      langWrap.querySelector("select")?.setAttribute("aria-label", "Language");
    }

    // Cart count: the mockup shows a "0" bubble even when empty.
    const cartBtn = header.querySelector("cart-icon");
    if (cartBtn && !cartBtn.querySelector(".rr-badge")) {
      cartBtn.appendChild(badge("cart", cartCount()));
    }
    syncCartBadge();
  }

  function init() {
    const header = document.querySelector("#header-component");
    if (!header) return;

    forceDrawer(header);
    injectWishlist(header);
    relocateLanguage(header);
    orderCluster(header);
    applyIcons(header);

    new MutationObserver(() => forceDrawer(header)).observe(header, {
      attributes: true,
      attributeFilter: ["data-menu-style"],
    });

    // Horizon re-renders parts of the header (notably the drawer) after mount,
    // which restores its own glyphs. Re-assert ours when that happens; swap()
    // is a no-op once the correct icon is in place, so this cannot loop.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        applyIcons(header);
      });
    }).observe(header, { childList: true, subtree: true });

    // The bag panel fetches /cart.js on open and publishes the count, which is
    // fresher than Horizon's bubble whenever an add went through our own POST.
    document.addEventListener("ruby:cart:count", (event) => {
      const count = event.detail && event.detail.count;
      if (count == null) return;
      freshCount = { count: String(count), bubbleAt: cartCount() };
      syncCartBadge();
    });

    window.addEventListener("resize", () => forceDrawer(header), { passive: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
